import React, { Suspense, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css"; // Tailwind styles
import "./i18n"; // Initialize i18next
import { initPromise } from "./i18n"; // Import the initialization promise
import AutoTranslateManager from "./components/AutoTranslateManager";
import GlobalErrorBoundary from "./components/GlobalErrorBoundary";
import { initializeTheme } from "./hooks/useTheme";
import { errorDev, logDev, warnDev } from "./utils/logger";
import { getFormattedVersion } from "./utils/version";

// Simple loading screen component
function LoadingScreen() {
  return (
    <div className="orrith-loading-screen" role="status" aria-live="polite">
      <div className="orrith-loading-brand">
        <img
          src="/logo.png"
          alt=""
          className="orrith-loading-logo"
          aria-hidden="true"
        />
      </div>
      <div className="orrith-loading-footer">
        <div className="orrith-loading-progress-track" aria-hidden="true">
          <div className="orrith-loading-progress-bar" />
        </div>
        <span className="orrith-loading-version">{getFormattedVersion()}</span>
        <span className="orrith-loading-subtext">
          Agricultural Operating Infrastructure
        </span>
      </div>
    </div>
  );
}

// App wrapper that waits for i18n initialization
function AppWrapper() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    initPromise.then(() => {
      setIsReady(true);
    }).catch((err) => {
      errorDev("Failed to initialize i18n:", err);
      // Proceed anyway to prevent blank screen
      setIsReady(true);
    });
  }, []);

  if (!isReady) {
    return <LoadingScreen />;
  }

  return <App />;
}

initializeTheme();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <GlobalErrorBoundary>
    <Suspense fallback={<LoadingScreen />}>
      <AppWrapper />
      {/* AutoTranslateManager is invisible UI-wise and runs translation tasks in the background */}
      <AutoTranslateManager />
    </Suspense>
  </GlobalErrorBoundary>
);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js?v=20261013-1', { scope: '/' })
      .then((registration) => {
        logDev('[PWA] Service Worker registered:', registration);

        // Check for existing waiting worker
        if (registration.waiting) {
          logDev('[PWA] Waiting worker found on registration');
          window.dispatchEvent(new CustomEvent('swupdate', { detail: { waitingWorker: registration.waiting } }));
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              logDev('[PWA] New version available');
              window.dispatchEvent(new CustomEvent('swupdate', { detail: { waitingWorker: newWorker } }));
            }
          });
        });

        // Listen for controller change (new SW took control)
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (refreshing) return;
          refreshing = true;
          logDev('[PWA] Controller changed, reloading...');
          window.location.reload();
        });
      })
      .catch((error) => {
        errorDev('[PWA] Service Worker registration failed:', error);
      });
  });
} else {
  warnDev('[PWA] Service Workers not supported in this browser');
}
